import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { chunk } from 'lodash-es';
import { VaPagination } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import { useFeatureToggle } from '~/platform/utilities/feature-toggles';
import { getClaimLetters } from '../../actions';
import ClaimsBreadcrumbs from '../../components/ClaimsBreadcrumbs';
import ClaimLetterList from '../../components/claim-letters/ClaimLetterList';
import ServiceUnavailableAlert from '../../components/ServiceUnavailableAlert';
import { ITEMS_PER_PAGE } from '../../constants';
import { isLoadingFeatures } from '../../selectors';
import NoLettersContent from './errorComponents/NoLettersContent';
import { setDocumentTitle } from '../../utils/helpers';
import { setPageFocus } from '../../utils/page';

const paginateItems = items => {
  return items?.length ? chunk(items, ITEMS_PER_PAGE) : [[]];
};

export const YourClaimLetters = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(isLoadingFeatures);
  const {
    data: letters,
    loading: lettersLoading,
    error,
  } = useSelector(state => state.disability.status.claimLetters);
  const [currentPage, setCurrentPage] = useState(1);

  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const iscstIncludeDdlBoaLettersEnabled = useToggleValue(
    TOGGLE_NAMES.cstIncludeDdlBoaLetters,
  );

  const claimLetterHeader = 'Your VA claim letters';
  const claimLetterSubText = iscstIncludeDdlBoaLettersEnabled
    ? 'You can download your claim letters and Board decisions here. We also mail you these letters. Refer to the date in your letter for official decisions or deadlines.'
    : 'You can download your claim letters. We also mail you these letters.';

  useEffect(() => {
    setDocumentTitle('Your VA Claim Letters');
    dispatch(getClaimLetters());
  }, [dispatch]);

  useEffect(() => {
    setPageFocus();
  });

  const paginatedItems = paginateItems(letters);
  const currentItems = paginatedItems[currentPage - 1] || [];
  const totalPages = paginatedItems.length;

  const onPageChange = page => {
    setCurrentPage(page);
  };

  const getSectionBodyContent = () => {
    if (error) {
      return (
        <ServiceUnavailableAlert
          heading="Your letters aren’t available to download right now"
          headerLevel={2}
        />
      );
    }

    return (
      <>
        {currentItems?.length ? (
          <ClaimLetterList letters={currentItems} />
        ) : (
          <NoLettersContent />
        )}
        {totalPages > 1 && (
          <VaPagination
            onPageSelect={e => onPageChange(e.detail.page)}
            page={currentPage}
            pages={totalPages}
          />
        )}
      </>
    );
  };

  if (isLoading) {
    return <va-loading-indicator message="Loading application..." />;
  }

  const crumb = {
    href: `../your-claim-letters`,
    label: `${claimLetterHeader}`,
    isRouterLink: true,
  };

  return (
    <article id="claim-letters" className="row vads-u-margin-bottom--5">
      <div className="usa-width-two-thirds medium-8 columns">
        <ClaimsBreadcrumbs crumbs={[crumb]} />
        <h1>{claimLetterHeader}</h1>
        <p className="vads-u-font-size--lg vads-u-margin-top--1 vads-u-margin-bottom--3">
          {claimLetterSubText}
        </p>
        {/* Shown to everyone with the flag on, not only users whose list has a
            Board decision: vets-api withholds a decision received before the
            cutoff or within the last 2 days, so the Veterans looking for one
            are the ones who won't see it in the list. "October 2026" is
            hardcoded and must match Settings.claim_letters.boa_decision_cutoff_date
            in vets-api — the frontend has no way to read it. If the release
            date moves, this copy has to move with it. */}
        {iscstIncludeDdlBoaLettersEnabled && (
          <va-details
            class="vads-u-display--block vads-u-margin-bottom--3"
            label="About Board decisions"
          >
            <p>
              Only Board decisions we issued after October 2026 are available
              here.
            </p>
            <p>
              Once we issue a decision, it may take up to 2 days for it to be
              available to download.
            </p>
          </va-details>
        )}
        {lettersLoading ? (
          <va-loading-indicator message="Loading your claim letters..." />
        ) : (
          getSectionBodyContent()
        )}
      </div>
    </article>
  );
};

export default YourClaimLetters;
